import {
    Control,
    FieldErrors,
    FieldValues,
    SubmitHandler,
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch
} from 'react-hook-form'
import { CustomDatePicker } from 'components/Datepicker'
import { InputLabel } from 'components/InputLabel'
import { TextArea } from 'components/TextArea'
import { InputSelect } from 'components/InputSelect'
import { Label } from 'components/InputLabel/styles'
import { Toggle } from 'components/Toggle'
import { VariableReducer } from 'types/variable'
import VariableActions from 'actions/Variable'
import { CustomToggle } from 'components/ToggleYesOrNot/CustomToggle'
import { BlueButton } from 'pages/NotifyEvent/styles'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducerState } from 'types/global'
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState
} from 'react'
import { TitleNotifications } from 'pages/Notifications/Header/styles'
import YellowCircleInterrogation from 'components/Icons/YellowCircleInterrogation'
import { NotifyEventType } from 'validator/notify_event_validator'
import { LocationReducer } from 'types/location'
import LocationActions from 'actions/Location'
import { SectorReducer } from 'types/sector'
import SectorActions from 'actions/Sector'
import NotificationService from 'services/Notification'
import { useNavigate } from 'react-router-dom'
import { ROUTES_NAMES } from 'constants/name.routes'
import { ICreateNewNotificationResponse } from 'types/notification'
import { inputRequiredStyle } from 'components/Datepicker/styles'
import GoUpComponent from 'components/ScrollUp'
import { useIsMobileScreen } from 'hooks/useMediaQuery'
import FormModals from './FormModals'
import FormPatientData from './FormPatientData'
import { InvolvedPatients } from './InvolvedPatients'
import { NotifyUpdateEventType } from 'validator/notify_complementary_infos'
import { Multiplier } from './Multiplier'
import { DAMAGE_LEVELS, filterDamageLevelOptions } from 'utils/damage'
import { variableListNumberPatientsInvolved } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/NotificationData/helper'
import { DropFile } from 'components/DropFile'
interface NotifyEventFormProps {
    trigger: any
    isDirty: boolean
    errors: FieldErrors<FieldValues>
    control: Control<FieldValues, any>
    watch: UseFormWatch<NotifyEventType>
    register: UseFormRegister<FieldValues>
    setValue: UseFormSetValue<NotifyEventType>
    getValues: UseFormGetValues<NotifyEventType>
    handleSubmit: UseFormHandleSubmit<NotifyEventType, undefined>
    setResponseData: Dispatch<SetStateAction<ICreateNewNotificationResponse>>
}
export default function NotifyEventForm({
    register,
    control,
    errors,
    handleSubmit,
    watch,
    setValue,
    getValues,
    isDirty,
    trigger,
    setResponseData,
}: NotifyEventFormProps) {
    const isMobile = useIsMobileScreen()

    // Campo comentado pois estava gravando versão antiga por conta do cache.
    // Por defaul o SN está gravando a versão mais recente
    // Podemos reativar essa funcionalidade caso pense em outra forma de pegar a variaveltalvez usando o dynamoDB para pegar isso
    // const NOTIFICATION_VERSION = process.env.REACT_APP_NOTIFICATION_VERSION || 'v4'

    const [showDamageLevelInfoModal, setShowDamageLevelInfoModal] = useState(false)
    const [showNotificationTypeInfoModal, setShowNotificationTypeInfoModal] = useState(false)
    const [showReviewBeforeSubmit, setShowReviewBeforeSubmit] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [showErrorMsg, setShowErrorMsg] = useState<boolean>(false)
    const [uploadS3ErrorMsg, setuploadS3ErrorMsg] = useState<boolean>(false)
    const [attachmentsURL, setAttachmentsURL] = useState<string>()

    //hooks
    const navigate = useNavigate()
    const watchFormData = watch()
    const dispatch = useDispatch()

    //redux state
    const variableSelector: VariableReducer = useSelector(
        (state: RootReducerState) => state.variableReducers
    )
    const locationSelector: LocationReducer = useSelector(
        (state: RootReducerState) => state.locationReducers
    )
    const sectorSelector: SectorReducer = useSelector(
        (state: RootReducerState) => state.sectorReducers
    )

    useEffect(() => {
        VariableActions.getAllVariables(dispatch)
        LocationActions.getAllLocations(dispatch, true)
    }, [])

    const incidentDate = watchFormData.date_incident
    const patientDate = watchFormData.patient_date_of_birth
    useEffect(() => {
        if (incidentDate && patientDate && incidentDate?.toISOString() < patientDate?.toISOString()) {
            setValue(
                'patient_date_of_birth',
                new Date(incidentDate?.toISOString())
            )
        }
    }, [incidentDate])

    const multiplierValidation = () => {
        return watchFormData.u_number_patients_involved ===
            variableListNumberPatientsInvolved(variableSelector)
                ?.choices?.[2]?.value &&
            watchFormData.involves_patient
    }

    const onChangeLocation = useCallback((value) => {
        setValue('location_incident', value, { shouldDirty: true })
        setValue('incident_sector', '')
        setValue('reporter_sector', '')
        SectorActions.getAllSectorsByLocationId(dispatch, value, "", "true")
    }, [])

    const submitForum: SubmitHandler<NotifyEventType> = async (data) => {
        data.notification_type = data.notification_type_toggle != null && (data.notification_type_toggle == true) ? 'identified' : 'anonymous'
        data.involves_patient = data.involves_patient != null ? data.involves_patient : false

        if (
            !data.involves_patient ||
            (data.involves_patient &&
                data.u_number_patients_involved ===
                variableListNumberPatientsInvolved(variableSelector)
                    ?.choices?.[2]?.value)
        ) {
            data.patient_date_of_birth = null
            data.document_number = ''
            data.document_type = ''
            data.patient_initials = ''
            data.patient_gender = ''
        }

        if (
            (data.involves_patient &&
                data.u_number_patients_involved ===
                variableListNumberPatientsInvolved(variableSelector)
                    ?.choices?.[1]?.value) ||
            !data.involves_patient
        ) {
            data.u_notification_multiplier = '1'
        } else {
            data.u_notification_multiplier = data.u_notification_multiplier
            data.patient_date_of_birth = null
            data.document_number = ''
            data.document_type = ''
            data.patient_initials = ''
            data.patient_gender = ''
        }

        if (!data.involves_patient) {
            data.u_number_patients_involved = ''
        }

        if (attachmentsURL) {
            data.u_attachments_url = attachmentsURL
            data.u_attachments_string = selectedFile.name
            data.u_attachments_size = selectedFile.size.toString()
        }

        // data.u_version = NOTIFICATION_VERSION

        const object = {
            sysparm_quantity: 1,
            variables: data
        }

        const response = await NotificationService.createNotification(object)
        const updateNotification = {
            u_notification_multiplier: object.variables.u_notification_multiplier,
            u_attachments_url: object.variables.u_attachments_url,
            u_attachments_string: object.variables.u_attachments_string,
            u_attachments_size: object.variables.u_attachments_size,
            // u_version: NOTIFICATION_VERSION
        }

        console.log("::: updateNotification", updateNotification)

        await NotificationService.updateNotificationGeneric(updateNotification, response.sys_id)

        setResponseData(response)

        navigate(ROUTES_NAMES.notifySent, { replace: true })
    }

    const handleGoToContinue = async () => {
        if (multiplierValidation() && selectedFile == null) {
            setShowErrorMsg(true)
        } else {
            setShowErrorMsg(false)
            const isValid = await trigger()
            if (isValid) {
                window.scrollTo(0, 0)
                setShowReviewBeforeSubmit(true)
            }
        }
    }

    useEffect(() => {
        if (showErrorMsg && uploadS3ErrorMsg) {
            setSelectedFile(null)
        }
    }, [showErrorMsg, uploadS3ErrorMsg]);

    const chooseDamageLevel = (id: string) => {
        setValue('damage_level', id, { shouldDirty: true })
        setShowDamageLevelInfoModal(false)
    }

    useEffect(() => {
        if (
            watch('damage_level') === DAMAGE_LEVELS[0].id ||
            watch('damage_level') === DAMAGE_LEVELS[1].id ||
            watch('damage_level') === DAMAGE_LEVELS[2].id
        ) {
            setValue('u_number_patients_involved', 'um_paciente')
        }
    }, [watch('damage_level')])

    const [progressValue, setProgressValue] = useState<number>(0)
    const [isDisabled, setIsDisabled] = useState<boolean>()

    useEffect(() => {
        if (selectedFile) {
            setIsDisabled(true)

            const uploadFile = async () => {
                try {
                    const { url } = await NotificationService.createPresignedURL(selectedFile, setProgressValue);

                    if (url) {
                        console.log("Anexo enviado com sucesso!");
                        setAttachmentsURL(url)
                        setShowErrorMsg(false)
                    }

                } catch (error) {
                    setShowErrorMsg(true);
                    setuploadS3ErrorMsg(true);
                    console.error("Erro ao enviar o anexo", error);
                } finally {
                    setIsDisabled(false)
                }
            };

            uploadFile();
        }

    }, [selectedFile]);

    useEffect(() => {
        if (attachmentsURL && selectedFile === null) {
            setProgressValue(0)

            const dropFile = async () => {
                try {
                    await NotificationService.deleteS3AttachedFile(attachmentsURL);
                    console.log("Anexo removido com sucesso!");
                    setProgressValue(0)
                } catch (error) {
                    console.error("Erro ao remover o anexo", error);
                } finally {
                    setIsDisabled(false)
                }
            };

            dropFile();
        }

    }, [selectedFile, attachmentsURL]);

    return (
        <div data-testid="notify-event-form">
            <FormModals
                isDirty={isDirty}
                isMobile={isMobile}
                getValues={getValues}
                submitForum={submitForum}
                handleSubmit={handleSubmit}
                watchFormData={watchFormData}
                chooseDamageLevel={chooseDamageLevel}
                showReviewBeforeSubmit={showReviewBeforeSubmit}
                showDamageLevelInfoModal={showDamageLevelInfoModal}
                setShowReviewBeforeSubmit={setShowReviewBeforeSubmit}
                setShowDamageLevelInfoModal={setShowDamageLevelInfoModal}
                showNotificationTypeInfoModal={showNotificationTypeInfoModal}
                setShowNotificationTypeInfoModal={setShowNotificationTypeInfoModal}
                thereIsFile={selectedFile != null}
            />
            <div className="pt-[18px]  md:px-8 px-6 relative">
                <TitleNotifications>Nova notificação</TitleNotifications>
                <p className="text-darkGray text-sm mt-1">
                    Preencha os campos abaixo com os dados do incidente
                </p>
            </div>
            <hr className={'border-[1px]  border-[#EBEDF2] mt-4'} />
            <div className="md:pt-[29px] mt-6 md:px-8 px-6 pb-6">
                <div className="flex gap-4 md:flex-row flex-col">
                    <div className="850px:w-1/5 md:w-2/5 w-1/2  z-10">
                        <CustomDatePicker
                            name="date_incident"
                            control={control}
                            required
                            label="Data da ocorrência"
                            placeholderData="Selecionar"
                            maxDate={new Date()}
                            isError={Boolean(errors.date_incident)}
                            error={Boolean(errors.date_incident)}
                            customStyle="text-sm"
                        />
                    </div>
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputLabel
                            label="Título da notificação"
                            name="incident_title"
                            register={register}
                            className={``}
                            placeholder="Digite aqui"
                            maxLength={80}
                            required
                            errorStyle="{ w-[741px] }"
                            error={{
                                message: errors.incident_title
                                    ? 'Preenchimento obrigatório.'
                                    : ''
                            }}
                        />
                    </div>
                </div>
                <div className="flex md:gap-4 gap-6 md:flex-row flex-col mt-6">
                    <div className="flex-1">
                        <TextArea
                            className="min-h-[257px]"
                            label="Descrição do incidente"
                            subLabel="Por favor, descreva o que aconteceu com o maior número de detalhes possíveis."
                            name="incident_description"
                            register={register}
                            placeholder="Digite aqui"
                            maxLength={2000}
                            required
                            rows={10}
                            inputLength={
                                2000 -
                                watchFormData.incident_description?.length
                            }
                            error={{
                                message: errors.incident_description
                                    ? 'Preenchimento obrigatório.'
                                    : ''
                            }}
                        />
                    </div>
                    <div className="flex-1 md:mt-0 mt-6">
                        <TextArea
                            className="min-h-[257px]"
                            label="Ação corretiva imediata"
                            subLabel="O que foi feito imediatamente após a ocorrência?"
                            name="immediate_corrective_action"
                            register={register}
                            placeholder="Digite aqui"
                            maxLength={2000}
                            required
                            rows={10}
                            inputLength={
                                2000 -
                                watchFormData.immediate_corrective_action
                                    ?.length
                            }
                            error={{
                                message: errors.immediate_corrective_action
                                    ? 'Preenchimento obrigatório.'
                                    : ''
                            }}
                        />
                    </div>
                </div>

                <div className="flex md:gap-4 gap-6 md:flex-row flex-col md:mt-6 mt-10 ">
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            label="Qual o Tipo do incidente"
                            name="incident_type"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={variableSelector.variablesListIncidentType
                                ?.slice(1)
                                .map((l) => ({
                                    value: l?.value,
                                    label: l?.label
                                }))
                                .sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.incident_type ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            label="Fator contribuinte (opcional)"
                            name="u_categorized_contributing_factor"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={variableSelector.variablesListContributingFactor
                                ?.slice(1)
                                .map((l) => ({
                                    value: l?.value,
                                    label: l?.label
                                }))
                                .sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.u_categorized_contributing_factor
                                ? inputRequiredStyle
                                : ''
                                }`}
                        />
                    </div>
                </div>
                <div className="flex mt-6 md:mr-4">
                    <div className="md:w-1/2 w-full relative">
                        <div className=" flex items-center text-center">
                            <Label className="mr-2">Classificação do Incidente</Label>
                            <div className="flex items-center cursor-pointer" onClick={() => { setShowDamageLevelInfoModal(true) }}>
                                <YellowCircleInterrogation />
                                <span className="ml-2 text-sm underline font-semibold text-blueTheme">
                                    Saiba mais
                                </span>
                            </div>

                        </div>
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            name="damage_level"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={filterDamageLevelOptions(
                                variableSelector.variablesListDamageLevel?.slice(1)?.map((l) => ({
                                    value: l?.value,
                                    label: l?.label
                                }))
                            )}
                            defaultValue={''}
                            errorStyle={`${errors.damage_level ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                </div>
                <hr className={'border-[1px]  border-[#EBEDF2] mt-6'} />
                <div className="mt-6">
                    <CustomToggle
                        control={control}
                        name={'involves_patient'}
                        enabled={watchFormData.involves_patient}
                        setEnabled={(b) => {
                            setValue('involves_patient', b, {
                                shouldDirty: true
                            })
                        }}
                        label="O incidente está relacionado a algum paciente?"
                        leftText="Não"
                        rightText="Sim"
                    />

                    <InvolvedPatients
                        variablePatientsInvolved={variableListNumberPatientsInvolved(
                            variableSelector
                        )}
                        watchFormData={watchFormData as NotifyUpdateEventType}
                        register={register}
                        errors={errors}
                    />

                    <FormPatientData
                        variableSelector={variableSelector}
                        errors={errors}
                        control={control}
                        register={register}
                        incidentDate={incidentDate}
                        involvePatient={watchFormData.involves_patient}
                        involvePatientMoreThenOne={
                            watchFormData?.u_number_patients_involved ===
                            variableListNumberPatientsInvolved(variableSelector)
                                ?.choices?.[2]?.value
                        }
                    />
                </div>
                <hr className={'border-[1px]  border-[#EBEDF2] mt-6'} />
                <div className="flex gap-4 md:flex-row flex-col mt-6">
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            onChange={onChangeLocation}
                            label="Hospital onde o evento ocorreu"
                            name="location_incident"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={locationSelector?.locationsList
                                ?.map((l) => ({
                                    value: l?.sys_id,
                                    label: l?.name
                                }))
                                ?.sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.location_incident
                                ? inputRequiredStyle
                                : ''
                                }`}
                        />
                    </div>
                    <div className="850px:w-4/5 md:w-3/5 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            label="Setor onde o evento ocorreu (setor notificado)"
                            name="incident_sector"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={sectorSelector.sectorsList
                                ?.map((l) => ({
                                    value: l?.sys_id,
                                    label: l?.u_name
                                }))
                                ?.sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.incident_sector ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                </div>
                <div className="w-full mt-4">
                    <Label>Turno que o evento ocorreu</Label>
                    <Toggle
                        isError={!!errors?.shift_incident}
                        required={true}
                        register={register}
                        name="shift_incident"
                        itens={variableSelector.variablesListNotificationTurn
                            ?.slice(1)
                            ?.map((t) => ({
                                id: t.index,
                                title: t.label,
                                value: t.value
                            }))}
                        defaultCheckedValue="NI"
                    />
                    <span className="text-errorText text-xs">
                        {errors.shift_incident
                            ? 'Preenchimento obrigatório.'
                            : ''}
                    </span>
                </div>

                <div className='grid sm:grid-cols-4 gap-4 mt-6'>
                    <Multiplier
                        containerClassName='col-span-2 gap-2'
                        setValue={setValue}
                        register={register}
                        showMultiplier={multiplierValidation()}
                        value={watchFormData.u_notification_multiplier}
                    />
                </div>

                <hr className={'border-[1px]  border-[#EBEDF2] mt-6'} />
                <div className="flex gap-4 md:flex-row flex-col mt-6">
                    <div className=" md:w-1/2 w-full">
                        <InputSelect
                            placeholder="Selecionar"
                            required={true}
                            styleBoxOptions="mt-3.5"
                            label="Seu setor (setor notificante)"
                            name="reporter_sector"
                            control={control}
                            shouldHaveSearch={true}
                            searchPlaceHolder="Buscar"
                            data={sectorSelector.sectorsList
                                ?.map((l) => ({
                                    value: l?.sys_id,
                                    label: l?.u_name
                                }))
                                ?.sort((o1, o2) =>
                                    o1.label?.localeCompare(o2.label)
                                )}
                            defaultValue={''}
                            errorStyle={`${errors.reporter_sector ? inputRequiredStyle : ''
                                }`}
                        />
                    </div>
                    <div className="md:w-1/2 w-full">
                        <div className="flex items-center">
                            <p className="line-clamp-1 font-semibold text-xs text-darkGray break-words mr-2" style={isMobile ? { maxWidth: '200px' } : {}}>
                                A sua notificação pode ser anônima ou identificada.
                            </p>
                            <div className="flex items-center cursor-pointer" onClick={() => { setShowNotificationTypeInfoModal(true) }}>
                                <YellowCircleInterrogation />
                                <span className="ml-2 text-sm underline font-semibold text-blueTheme">
                                    Saiba mais
                                </span>
                            </div>
                        </div>
                        <CustomToggle
                            control={control}
                            name={'notification_type_toggle'}
                            enabled={watchFormData.notification_type_toggle}
                            setEnabled={(b) => {
                                setValue('notification_type_toggle', b, { shouldDirty: true })
                                setValue('reporter_name', '')
                            }}
                            leftText="Anônima"
                            rightText="Quero me identificar"
                        />
                    </div>
                </div>
                {watchFormData.notification_type_toggle ? (
                    <div className="mt-6">
                        <InputLabel
                            label="Seu nome completo"
                            name="reporter_name"
                            register={register}
                            className={``}
                            placeholder="Digite aqui"
                            maxLength={80}
                            required
                            errorStyle="{ w-[741px] }"
                            error={{
                                message: errors.reporter_name
                                    ? 'Preenchimento obrigatório. Caso não queira se identificar, volte e selecione a opção "Notificação anônima.'
                                    : ''
                            }}
                        />
                    </div>
                ) : null}

                <Label className='mt-4 mb-2'>
                    {`Anexar Documento ${!multiplierValidation() ? '(opcional)' : ''}`}
                    <br />
                    {
                        showErrorMsg &&
                        <p className='text-wrap	text-errorText font-normal text-xs mt-1'>
                            {`${uploadS3ErrorMsg ?
                                'O envio do anexo falhou. Verifique a sua conexão e tente novamente.' :
                                'Anexo de lista de pacientes relacionados ao incidente (obrigatório)'}`}
                        </p>
                    }
                </Label>
                <DropFile
                    onFileSelect={(file: File) => setSelectedFile(file)}
                    errorFlag={showErrorMsg}
                    setErrorFlag={setShowErrorMsg}
                    isMobile={isMobile}
                    progressValue={progressValue}
                    setProgressValue={setProgressValue}
                />

                <div className="mt-11 flex gap-4 items-center">
                    <p
                        onClick={() => {
                            navigate(ROUTES_NAMES.login)
                        }}
                        className="underline text-blueTheme text-sm cursor-pointer"
                    >
                        Sair
                    </p>
                    <BlueButton
                        type="button"
                        className={`w-[363px] ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={handleGoToContinue}
                        disabled={isDisabled}
                    >
                        Continuar
                    </BlueButton>
                </div>
            </div>
            <GoUpComponent />
        </div>
    )
}
